import { Injectable, signal } from '@angular/core';

/**
 * Describes the route parameters of an event deep link.
 * `taskId` and `eventId` are only present for the
 * `/monitors/:workflowId/tasks/:taskId/events/:eventId` shape.
 */
export interface MonitorsEventParams {
  workflowId: string;
  taskId?: string;
  eventId?: string;
}

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  // Queue of pending internal paths – supports multiple deep link events
  private pending = signal<string[]>([]);

  /** Stores a deep link URL to be consumed after hydration. */
  setDeepLink(rawUrl: string): void {
    const internalPath = this.normalizeUrl(rawUrl);
    this.pending.update(queue => {
      // Avoid pushing duplicates of the same path
      if (queue.includes(internalPath)) return queue;
      return [...queue, internalPath];
    });
  }

  /** Removes and returns the first pending link, or null if the queue is empty. */
  consumeDeepLink(): string | null {
    let link: string | null = null;
    this.pending.update(queue => {
      if (queue.length === 0) return queue;
      link = queue[0];
      return queue.slice(1);
    });
    return link;
  }

  /**
   * Converts a raw URL (full absolute URL, custom scheme URL, or already relative path)
   * into an internal Angular router path, stripping out any protocol, host, and base.
   *
   * Examples:
   *   'https://rtila.app/monitors/abc/events?ids=1' → '/monitors/abc/events?ids=1'
   *   'https://rtila.app/monitors/abc/tasks/def/events/ghi' → '/monitors/abc/tasks/def/events/ghi'
   *   'capacitor://localhost/monitors/abc/events' → '/monitors/abc/events'
   *   '/monitors/abc/events' → '/monitors/abc/events'
   */
  normalizeUrl(raw: string): string {
    if (!raw) return '';

    try {
      // Standard http(s) URL
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        const url = new URL(raw);
        return url.pathname + url.search + url.hash;
      }

      // Already a relative internal path
      if (raw.startsWith('/')) {
        return raw;
      }

      // Custom scheme (e.g., capacitor://, rtila://). Remove scheme and host.
      const colonIdx = raw.indexOf('://');
      if (colonIdx !== -1) {
        const afterScheme = raw.substring(colonIdx + 3);
        const slashIdx = afterScheme.indexOf('/');
        if (slashIdx !== -1) {
          return afterScheme.substring(slashIdx);
        }
        return '/';
      }

      // Fallback – treat as path
      return raw.startsWith('/') ? raw : '/' + raw;
    } catch {
      // Parsing failed (e.g. malformed URL). Fallback: strip any protocol prefix.
      const withoutProtocol = raw.replace(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//, '');
      const pathStart = withoutProtocol.indexOf('/');
      if (pathStart !== -1) {
        return withoutProtocol.substring(pathStart);
      }
      return '/' + withoutProtocol;
    }
  }

  /**
   * Parses a normalised monitors‑events path and returns the route parameters.
   * Returns `null` if the path doesn’t match one of the known patterns.
   *
   * Supported patterns:
   *   - /monitors/:workflowId/events
   *   - /monitors/:workflowId/tasks/:taskId/events/:eventId
   *
   * @param path A normalised internal path (may contain query/hash, which are ignored).
   */
  parseMonitorsEventPath(path: string): MonitorsEventParams | null {
    if (!path) return null;

    // Strip query string and hash so we only match the path segments
    const pathOnly = path.split('?')[0].split('#')[0];

    // Match the new deep‑link pattern with task & event IDs
    const extendedMatch = pathOnly.match(
      /^\/monitors\/([^\/]+)\/tasks\/([^\/]+)\/events\/([^\/]+)$/
    );
    if (extendedMatch) {
      return {
        workflowId: extendedMatch[1],
        taskId: extendedMatch[2],
        eventId: extendedMatch[3],
      };
    }

    // Match the legacy pattern (no task / event)
    const legacyMatch = pathOnly.match(
      /^\/monitors\/([^\/]+)\/events$/
    );
    if (legacyMatch) {
      return { workflowId: legacyMatch[1] };
    }

    return null;
  }
}
